import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeamActivity, getTeamActivityPerson } from '../api';
import KpiRow from '../components/ar/ArKpiRow';
import { SegmentedControl, StateScreen } from '../components/ui';
import { ModalShell, SecondaryButton } from '../components/subs/SubUi';

/* ═══════════════════════════════════════════════════════════════════════════
   Team Activity (Duncan 2026-10-22: "a full audit login usage meter just like Facility Navigator has";
   the same people and place as the Audit Log). Who uses Summit and what they do over the last 7, 30 or
   90 days: four totals, sign-ins per day, a roster (sign-ins, days used, changes, jobs, what they mostly
   work on, last seen) and the people who haven't used it. A person opens their days and everything they
   did; a line opens that day of theirs in the Audit Log. From routes/teamActivity.js. Days are Central.
   ═══════════════════════════════════════════════════════════════════════════ */

const RANGES = [{ value: 7, label: 'Last 7 days' }, { value: 30, label: 'Last 30 days' }, { value: 90, label: 'Last 90 days' }];
const ROLE = {
  admin: 'Admin', vp: 'Vice President', construction_director: 'Const. Director', construction_manager: 'Const. Manager',
  pm: 'Project Manager', accounting: 'Accounting', e3_direct_director: 'E3 Direct Director', e3_direct_pm: 'E3 Direct PM',
  sales_manager: 'Sales Manager', sales_person: 'Sales', project_developer: 'Project Developer',
};
const ACTION = {
  login: 'Signed in', create: 'Added', update: 'Changed', delete: 'Removed', status_change: 'Changed the status of',
  export: 'Exported', view: 'Viewed', afp_signature_routed: 'Sent for signature',
};
const card = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
const microLabel = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };
const COLS = 'minmax(0, 1.6fr) 118px 64px 80px 56px minmax(0, 1.2fr) 104px';

const at = (ymd) => new Date(`${ymd}T12:00:00`);
const weekend = (ymd) => [0, 6].includes(at(ymd).getDay());
const dayLabel = (ymd) => at(ymd).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const shortDay = (ymd) => at(ymd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' });
const plural = (n, word, many = `${word}s`) => `${n.toLocaleString()} ${n === 1 ? word : many}`;
/** "12 min ago", "3 h ago", "8 days ago", then the date; "never". */
function ago(iso, now = Date.now()) {
  if (!iso) return 'never';
  const min = Math.round((now - Date.parse(iso)) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* Bars by day, weekends lighter; hovering a day says its numbers. `parts`: [{ key, color }] stacked bottom up. */
function DayBars({ days, parts, height = 96, tipOf, label }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...days.map((d) => parts.reduce((n, p) => n + (d[p.key] || 0), 0)));
  const W = 1000;
  const slot = W / days.length;
  const bw = Math.max(2, Math.min(28, slot - 3));
  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${height}`} width="100%" height={height} preserveAspectRatio="none" role="img" aria-label={label} style={{ display: 'block' }}>
        <line x1="0" y1={height - 0.5} x2={W} y2={height - 0.5} stroke="var(--border-default)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {days.map((d, i) => {
          let y = height - 1;
          return (
            <g key={d.day} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={i * slot} y="0" width={slot} height={height} fill="transparent" />
              {parts.map((p) => {
                const h = ((d[p.key] || 0) / max) * (height - 6);
                y -= h;
                return h > 0 ? <rect key={p.key} x={i * slot + (slot - bw) / 2} y={y} width={bw} height={h} rx="2"
                  fill={p.color} opacity={weekend(d.day) ? 0.4 : hover === i || hover === null ? 1 : 0.75} /> : null;
              })}
            </g>
          );
        })}
      </svg>
      {hover !== null && (
        <div role="tooltip" style={{
          position: 'absolute', bottom: height + 4, left: `${((hover + 0.5) / days.length) * 100}%`, transform: 'translateX(-50%)',
          pointerEvents: 'none', whiteSpace: 'nowrap', padding: '6px 10px', borderRadius: 7, fontSize: 12.5, fontWeight: 600,
          color: 'var(--bg-surface)', background: 'var(--text-1)', boxShadow: 'var(--shadow-md)', zIndex: 5,
        }}>{tipOf(days[hover])}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11.5, color: 'var(--text-4)' }}>
        <span>{shortDay(days[0].day)}</span><span>{shortDay(days[days.length - 1].day)}</span>
      </div>
    </div>
  );
}

const SORTS = {
  name: (a, b) => a.name.localeCompare(b.name),
  signins: (a, b) => b.signins - a.signins,
  days_active: (a, b) => b.days_active - a.days_active,
  changes: (a, b) => b.changes - a.changes,
  projects: (a, b) => b.projects - a.projects,
  last_seen: (a, b) => String(b.last_seen || '').localeCompare(String(a.last_seen || '')),
};

/* Everyone who used Summit in the range; a row opens their days and what they did. Headers sort. */
function Roster({ people, onOpen }) {
  const [sort, setSort] = useState('signins');
  const rows = useMemo(() => [...people].sort((a, b) => SORTS[sort](a, b) || SORTS.name(a, b)), [people, sort]);
  const maxSignins = Math.max(1, ...people.map((p) => p.signins));
  const head = (key, text, align) => (
    <button type="button" onClick={() => setSort(key)} aria-pressed={sort === key}
      style={{ ...microLabel, fontSize: 11, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: align || 'left', color: sort === key ? 'var(--text-1)' : 'var(--text-3)' }}>
      {text}{sort === key ? ' ↓' : ''}
    </button>
  );
  return (
    <section style={{ ...card, overflow: 'hidden' }} aria-label="People">
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 720 }}>
          <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, padding: '10px 18px', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-default)' }}>
            {head('name', 'Person')}{head('signins', 'Sign-ins')}{head('days_active', 'Days')}{head('changes', 'Changes')}
            {head('projects', 'Jobs')}<span style={{ ...microLabel, fontSize: 11 }}>Mostly</span>{head('last_seen', 'Last seen')}
          </div>
          {rows.map((p) => (
            <div key={p.user_id} className="ta-row" role="button" tabIndex={0} aria-label={`Open ${p.name}'s activity`}
              onClick={() => onOpen(p)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p); }}
              style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', padding: '11px 18px', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: 14 }}>
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontWeight: 650, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ROLE[p.role] || p.role}{p.active ? '' : ' · no longer active'}</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 58, height: 6, borderRadius: 4, background: 'var(--bg-surface-2)', overflow: 'hidden', flexShrink: 0 }}>
                  <span style={{ display: 'block', width: `${(p.signins / maxSignins) * 100}%`, height: '100%', background: 'var(--accent)', borderRadius: 4 }} />
                </span>
                <span className="mono" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-1)' }}>{p.signins}</span>
              </span>
              <span className="mono" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{p.days_active}</span>
              <span className="mono" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{p.changes.toLocaleString()}</span>
              <span className="mono" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{p.projects}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.mostly.join(', ') || '—'}</span>
              <span style={{ color: 'var(--text-2)', fontSize: 13, whiteSpace: 'nowrap' }} title={p.last_seen ? new Date(p.last_seen).toLocaleString() : undefined}>{ago(p.last_seen)}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding: '9px 18px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-3)' }}>
        Click a person for their days and everything they did. Days used counts sign-ins, changes and the days Summit saw them.
      </div>
    </section>
  );
}

/* One person: their days, then everything they did, a day at a time; a line opens their day in the Audit Log. */
function PersonDialog({ person, days, onClose, onOpenProject }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    getTeamActivityPerson(person.user_id, days).then(setData).catch((e) => setError(e.message || 'Couldn’t load their activity'));
  }, [person.user_id, days]);
  const byDay = useMemo(() => {
    const m = new Map();
    (data ? data.events : []).forEach((e) => m.set(e.day, [...(m.get(e.day) || []), e]));
    return [...m];
  }, [data]);
  const openLog = (day) => { onClose(); navigate(`/audit-log?user_id=${person.user_id}&day=${day}`); };
  return (
    <ModalShell title={person.name} subtitle={`${ROLE[person.role] || person.role || ''} · last seen ${ago(person.last_seen)} · the last ${days} days`}
      onClose={onClose} width={860} footer={<SecondaryButton onClick={onClose}>Close</SecondaryButton>}>
      {error ? <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>
        : !data ? <div style={{ fontSize: 14, color: 'var(--text-3)' }}>Loading…</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={microLabel}>Their days</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-3)' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--accent)' }} />changes</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-3)' }}><span style={{ width: 10, height: 10, borderRadius: 3, background: 'color-mix(in oklch, var(--accent) 45%, var(--bg-surface))' }} />sign-ins</span>
              </div>
              <DayBars days={data.daily} height={70} label={`${person.name}'s sign-ins and changes per day`}
                parts={[{ key: 'signins', color: 'color-mix(in oklch, var(--accent) 45%, var(--bg-surface))' }, { key: 'changes', color: 'var(--accent)' }]}
                tipOf={(d) => `${dayLabel(d.day)} · ${plural(d.signins, 'sign-in')} · ${plural(d.changes, 'change')}`} />
            </div>
            {!byDay.length ? <div style={{ fontSize: 14, color: 'var(--text-3)' }}>Nothing in the audit log for them in this range{data.daily.some((d) => d.seen) ? ', though Summit saw them on some days (looking, not changing)' : ''}.</div> : byDay.map(([day, events]) => (
              <div key={day}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '0 0 4px' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)' }}>{dayLabel(day)}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{plural(events.length, 'entry', 'entries')}</span>
                  <span style={{ flex: 1 }} />
                  <button type="button" onClick={() => openLog(day)} style={{ background: 'none', border: 'none', padding: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', cursor: 'pointer' }}>Open this day in the Audit Log</button>
                </div>
                {events.map((e) => (
                  <div key={e.id} className="ta-line" role="button" tabIndex={0} onClick={() => openLog(day)} onKeyDown={(ev) => { if (ev.key === 'Enter') openLog(day); }}
                    title="Open this day in the Audit Log"
                    style={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', gap: 10, padding: '6px 8px', borderTop: '1px solid var(--border-subtle)', fontSize: 13.5, cursor: 'pointer', borderRadius: 6 }}>
                    <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12.5 }}>{timeOf(e.created_at)}</span>
                    <span style={{ minWidth: 0, color: 'var(--text-2)' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{ACTION[e.action] || e.action}</span>
                      {e.action !== 'login' && <span style={{ color: 'var(--text-3)' }}> · {e.area}</span>}
                      {e.entity_name && <> · {e.entity_name}</>}
                      {e.project_id && e.project_name && (
                        <> · <button type="button" onClick={(ev) => { ev.stopPropagation(); onClose(); onOpenProject(e.slug || e.project_id); }}
                          style={{ background: 'none', border: 'none', padding: 0, fontSize: 13.5, color: 'var(--accent-text)', cursor: 'pointer' }}>
                          {e.project_number ? `${e.project_number} ` : ''}{e.project_name}</button></>
                      )}
                      {e.details && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.details}>{e.details}</span>}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
    </ModalShell>
  );
}

const TeamActivityPage = ({ onOpenProject }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [allQuiet, setAllQuiet] = useState(false);

  const load = useCallback(() => {
    setError(null);
    return getTeamActivity(days).then(setData).catch((e) => setError(e.message || 'Couldn’t load team activity'));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (error && !data) return <StateScreen kind="error" title="Couldn’t load team activity" body={error} />;
  if (!data) return <div style={{ padding: 24, fontSize: 14, color: 'var(--text-3)' }}>Loading team activity…</div>;
  const { totals, daily, people, quiet } = data;
  const workdays = Math.max(1, daily.filter((d) => !weekend(d.day)).length);
  const quietShown = allQuiet ? quiet : quiet.slice(0, 12);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{`
        .ta-row:hover, .ta-line:hover { background: color-mix(in oklch, var(--bg-hover) 60%, transparent); }
        .ta-row:focus-visible, .ta-line:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--accent-ring); }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 15, color: 'var(--text-3)', flex: 1, minWidth: 240 }}>
          Who uses Summit and what they do: sign-ins and changes from the Audit Log, and the days Summit saw each person.
        </p>
        <SegmentedControl value={days} onChange={setDays} options={RANGES} />
      </div>
      <KpiRow label="Team activity totals" tiles={[
        { label: 'People active', value: `${totals.active} of ${totals.users}`, sub: 'used Summit in the range' },
        { label: 'Sign-ins', value: totals.signins.toLocaleString(), sub: `about ${Math.round(totals.signins / workdays)} a working day` },
        { label: 'Changes made', value: totals.changes.toLocaleString(), sub: 'schedules, billing, SOV, documents…' },
        { label: 'Jobs changed', value: totals.projects.toLocaleString(), sub: 'by anyone' },
      ]} />
      <section style={{ ...card, padding: '14px 18px' }} aria-label="Sign-ins per day">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={microLabel}>Sign-ins per day</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>weekends lighter · hover a day</span>
        </div>
        <DayBars days={daily} parts={[{ key: 'signins', color: 'var(--accent)' }]} label="Sign-ins per day"
          tipOf={(d) => `${dayLabel(d.day)} · ${plural(d.signins, 'sign-in')} · ${plural(d.people, 'person', 'people')}`} />
      </section>
      {people.length ? <Roster people={people} onOpen={setOpen} /> : (
        <div style={{ ...card, padding: '18px', fontSize: 14, color: 'var(--text-3)' }}>Nobody used Summit in this range.</div>
      )}
      {quiet.length > 0 && (
        <section style={{ ...card, padding: '12px 18px', borderStyle: 'dashed', boxShadow: 'none' }} aria-label="Not using Summit">
          <div style={{ fontSize: 14, color: 'var(--text-2)', marginBottom: 6 }}>
            <strong style={{ fontWeight: 700, color: 'var(--text-1)' }}>{plural(quiet.length, 'person', 'people')}</strong>
            {' '}{quiet.length === 1 ? "hasn't" : "haven't"} used Summit in the last {days} days
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', fontSize: 13.5 }}>
            {quietShown.map((q) => (
              <span key={q.user_id} style={{ color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                {q.name} <span style={{ color: 'var(--text-4)' }}>{q.last_seen ? `last ${ago(q.last_seen)}` : 'never'}</span>
              </span>
            ))}
            {quiet.length > quietShown.length && (
              <button type="button" onClick={() => setAllQuiet(true)} style={{ background: 'none', border: 'none', padding: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--accent-text)', cursor: 'pointer' }}>
                Show all {quiet.length}
              </button>
            )}
          </div>
        </section>
      )}
      {open && <PersonDialog person={open} days={days} onClose={() => setOpen(null)} onOpenProject={onOpenProject} />}
    </div>
  );
};

export default TeamActivityPage;
